import React from "react";
import { CheckCircle2, AlertTriangle, XCircle, Send, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useT } from "../lib/lang";

const Row = ({ d, t }) => {
  const icon =
    d.status === "sent" ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
      : d.status === "skipped" ? <AlertTriangle className="w-4 h-4 text-amber-600" />
      : <XCircle className="w-4 h-4 text-rescue-red" />;
  const label =
    d.channel === "in_app" ? t.inAppLog
      : d.channel === "sms" ? t.channelSms
      : t.channelEmail;
  return (
    <div className="flex items-start justify-between gap-2 py-2 border-b border-rescue-line last:border-b-0">
      <div className="flex items-start gap-2 min-w-0">
        {icon}
        <div className="min-w-0">
          <div className="text-xs font-bold uppercase tracking-[0.15em]">
            {label}{d.name ? ` · ${d.name}` : ""}
          </div>
          <div className="font-mono text-[11px] text-rescue-mute truncate">{d.target}</div>
          {d.detail && d.status !== "sent" && (
            <div className="text-[11px] text-rescue-mute mt-0.5">{d.detail}</div>
          )}
        </div>
      </div>
      <span className={`text-[10px] font-bold uppercase tracking-[0.2em] ${
        d.status === "sent" ? "text-emerald-700"
          : d.status === "skipped" ? "text-amber-700"
          : "text-rescue-red"
      }`}>{t.delivery[d.status] || d.status}</span>
    </div>
  );
};

export const AlertResultModal = ({ alert, onClose }) => {
  const [t, labels] = useT();
  if (!alert) return null;
  const summary = alert.status_summary;
  const summaryLabel =
    summary === "delivered" ? t.statusDelivered
      : summary === "partial" ? t.statusPartial
      : t.statusInAppOnly;
  const summaryColor =
    summary === "delivered" ? "bg-emerald-600"
      : summary === "partial" ? "bg-amber-600"
      : "bg-rescue-ink";
  const typeLabel = labels[alert.emergency_type]?.label || alert.emergency_type;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="alert-result-modal">
      <div className="w-full max-w-md bg-white border-t-2 sm:border-2 border-rescue-ink max-h-full overflow-y-auto pb-[env(safe-area-inset-bottom)]">
        <div className={`${summaryColor} text-white px-5 py-4 flex items-center justify-between`}>
          <div>
            <div className="text-[10px] tracking-[0.25em] font-bold uppercase opacity-90">{t.status}</div>
            <div className="font-display text-2xl font-black tracking-tight">{summaryLabel}</div>
          </div>
          <button onClick={onClose} data-testid="close-alert-modal" className="p-2 hover:bg-white/10 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 border-b-2 border-rescue-line">
          <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-rescue-mute mb-1">{t.type}</div>
          <div className="font-display text-3xl font-black">{typeLabel}</div>
          {alert.description && <p className="mt-2 text-sm text-rescue-ink">{alert.description}</p>}
        </div>

        <div className="px-5 py-4 border-b-2 border-rescue-line font-mono text-xs">
          <div className="flex justify-between"><span className="text-rescue-mute">LAT</span><span>{alert.coordinates.latitude.toFixed(6)}</span></div>
          <div className="flex justify-between"><span className="text-rescue-mute">LNG</span><span>{alert.coordinates.longitude.toFixed(6)}</span></div>
          {alert.coordinates.accuracy && (
            <div className="flex justify-between"><span className="text-rescue-mute">±</span><span>{Math.round(alert.coordinates.accuracy)} m</span></div>
          )}
        </div>

        <div className="px-5 py-4">
          <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-rescue-mute mb-2">{t.deliveryReport}</div>
          {alert.deliveries.map((d, i) => <Row key={`${d.channel}-${d.target}-${i}`} d={d} t={t} />)}
        </div>

        <div className="p-4 grid grid-cols-2 gap-2 border-t-2 border-rescue-line">
          <a
            href={alert.map_link}
            target="_blank"
            rel="noreferrer"
            data-testid="open-map-btn"
            className="border-2 border-rescue-ink text-rescue-ink py-3 text-center text-xs font-bold uppercase tracking-[0.2em] hover:bg-rescue-ink hover:text-white transition-colors"
          >
            {t.openMap}
          </a>
          <Link
            to={`/share/${alert.id}`}
            onClick={onClose}
            data-testid="open-share-btn"
            className="bg-rescue-ink text-white py-3 text-center text-xs font-bold uppercase tracking-[0.2em] hover:bg-rescue-red transition-colors flex items-center justify-center gap-2"
          >
            <Send className="w-4 h-4" /> {t.shareLink}
          </Link>
        </div>
      </div>
    </div>
  );
};
